import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { Box, Button, ButtonBase, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { layoutTokens, typographyTokens } from "@/shared/theme";

export type ExplorerGroup<T extends { id: string }> = { id: string; label: string; items: readonly T[] };

export interface EvidenceExplorerProps<T extends { id: string }> {
  label: string;
  groups: readonly ExplorerGroup<T>[];
  selectedId: string | null;
  /** Phones: list and panel take turns at full width. */
  compact: boolean;
  backLabel: string;
  toolbar?: ReactNode;
  emptyPanel: ReactNode;
  /** The page's scroll owner, so returning to the list restores where the reader was. */
  scrollContainer?: HTMLElement | null;
  onSelect: (id: string) => void;
  onBack: () => void;
  itemLabel: (item: T) => string;
  renderRow: (item: T) => ReactNode;
  renderPanel: (item: T) => ReactNode;
}

export const useExplorerCompact = () => useMediaQuery(useTheme().breakpoints.down("md"));

const labelSx = {
  fontSize: "caption.fontSize",
  fontWeight: "fontWeightBold",
  letterSpacing: typographyTokens.tracking.label,
} as const;

/** List-detail evidence layout: one list and one panel that stays put while the selection changes. */
export const EvidenceExplorer = <T extends { id: string }>({
  label,
  groups,
  selectedId,
  compact,
  backLabel,
  toolbar,
  emptyPanel,
  scrollContainer,
  onSelect,
  onBack,
  itemLabel,
  renderRow,
  renderPanel,
}: EvidenceExplorerProps<T>) => {
  const selected = groups.flatMap((group) => group.items).find((item) => item.id === selectedId) ?? null;
  const showDetail = compact && selected !== null;
  const listScroll = useRef(0);
  const panelRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [selectedId]);
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!compact || !scrollContainer) return;
    // The detail view starts at the explorer's own top, so the back button and board fill the screen.
    const explorerTop = rootRef.current
      ? scrollContainer.scrollTop +
        rootRef.current.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top
      : 0;
    scrollContainer.scrollTop = showDetail ? explorerTop : listScroll.current;
  }, [compact, showDetail, scrollContainer]);
  // Remember where the list was, whatever opens the detail (a row, a summary tile, a tracked card).
  useEffect(() => {
    if (!compact || showDetail || !scrollContainer) return;
    const remember = () => {
      listScroll.current = scrollContainer.scrollTop;
    };
    remember();
    scrollContainer.addEventListener("scroll", remember, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", remember);
  }, [compact, showDetail, scrollContainer]);
  const select = (id: string) => onSelect(id);

  const list = (
    <Box
      component="nav"
      aria-label={label}
      data-tour="explorer-list"
      // The page's smooth scroller captures wheel events; the list scrolls natively.
      data-lenis-prevent={compact ? undefined : true}
      // Wide screens scroll the list inside the workspace so the panel never moves.
      sx={{ minWidth: 0, ...(compact ? {} : { height: "100%", overflowY: "auto", pr: 0.5 }) }}
    >
      {toolbar}
      {groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <Box key={group.id} component="section" aria-label={group.label} sx={{ mt: 2 }}>
            <Typography component="h3" sx={{ ...labelSx, color: "text.secondary", mb: 0.75 }}>
              {group.label.toUpperCase()}
            </Typography>
            <Box sx={{ border: layoutTokens.dividerBorder, borderColor: "divider" }}>
              {group.items.map((item) => {
                const active = item.id === selectedId;
                return (
                  <ButtonBase
                    key={item.id}
                    data-explorer-item={item.id}
                    aria-pressed={active}
                    aria-label={itemLabel(item)}
                    onClick={() => select(item.id)}
                    sx={(theme) => ({
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      p: 1.5,
                      position: "relative",
                      borderBottom: `${layoutTokens.dividerBorder}px solid ${theme.palette.divider}`,
                      "&:last-of-type": { borderBottom: "none" },
                      bgcolor: active ? alpha(theme.palette.secondary.main, 0.06) : "transparent",
                      boxShadow: active
                        ? `inset 0 0 0 ${layoutTokens.structuralBorder}px ${theme.palette.secondary.main}`
                        : "none",
                      "&:hover": {
                        bgcolor: active ? alpha(theme.palette.secondary.main, 0.1) : theme.palette.action.hover,
                      },
                      "&.Mui-focusVisible": {
                        outline: `${layoutTokens.structuralBorder}px solid ${theme.palette.primary.main}`,
                        outlineOffset: -layoutTokens.structuralBorder,
                      },
                    })}
                  >
                    {active && (
                      <Typography
                        component="span"
                        sx={{ ...labelSx, color: "secondary.main", position: "absolute", top: 8, right: 12 }}
                      >
                        VIEWING
                      </Typography>
                    )}
                    {renderRow(item)}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        ))}
    </Box>
  );

  const panel = (
    <Box
      ref={panelRef}
      component="section"
      aria-label="Evidence"
      data-tour="explorer-panel"
      data-testid="explorer-panel"
      data-lenis-prevent={compact ? undefined : true}
      sx={{
        minWidth: 0,
        // Lets the evidence inside switch between stacked and side-by-side by the panel's own width.
        containerType: "inline-size",
        ...(compact ? {} : { height: "100%", overflowY: "auto" }),
      }}
    >
      {selected ? renderPanel(selected) : emptyPanel}
    </Box>
  );

  if (compact)
    return showDetail ? (
      <Box ref={rootRef} data-evidence-explorer>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1.5 }}>
          {backLabel}
        </Button>
        {panel}
      </Box>
    ) : (
      <Box data-evidence-explorer>{list}</Box>
    );
  return (
    <Box
      data-evidence-explorer
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(260px, 320px) minmax(0, 1fr)",
        gap: 2,
        // One screen tall: once scrolled into view, the list and the board both stay on screen.
        height: "calc(100dvh - 32px)",
        scrollMarginTop: 16,
      }}
    >
      {list}
      {panel}
    </Box>
  );
};
